import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/auth-store';
import { useTodayAttendance, useClockAction } from '@/hooks/useAttendance';
import { checkGeofence } from '@/services/geofence';
import '@/styles/attendance.css';

function formatClock(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function todayLabel(): string {
  return new Date().toLocaleDateString([], {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

export function AttendanceRoute() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const employeeId = user?.employeeId ?? null;
  const { data: today, isLoading } = useTodayAttendance(employeeId);
  const clock = useClockAction(employeeId);

  if (!user) return <div className="att-page" aria-busy="true" />;

  if (!employeeId) {
    return (
      <div className="att-page">
        <div className="att-empty">
          <span className="material-symbols-outlined">badge</span>
          <div className="att-empty-title">{t('attendance.no_employee_title')}</div>
          <div className="att-empty-body">{t('attendance.no_employee_body')}</div>
        </div>
      </div>
    );
  }

  const clockedIn = Boolean(today?.clockIn);
  const clockedOut = Boolean(today?.clockOut);
  const nextAction: 'in' | 'out' = clockedIn && !clockedOut ? 'out' : 'in';
  const canAct = !clockedOut;

  async function onClock() {
    const coords: { lat?: number; lng?: number } = {};

    // Geofence + GPS capture only on clock-in. Clocking out from anywhere is
    // fine (worker may be walking out the gate when they tap).
    if (nextAction === 'in') {
      const fence = await checkGeofence(user!.tenant);
      if (fence.ok) {
        coords.lat = fence.lat;
        coords.lng = fence.lng;
      } else if (fence.reason === 'too-far' && typeof fence.distanceM === 'number') {
        const km = (fence.distanceM / 1000).toFixed(1);
        const ok = window.confirm(t('attendance.geofence_far_confirm', { km }));
        if (!ok) return;
      }
      // `denied` / `unavailable` / `timeout` silently allowed — worker may have
      // GPS off for privacy, or be deep inside a warehouse with no signal.
    }

    clock.mutate(
      { action: nextAction, ...coords },
      {
        onSuccess: () => {
          toast.success(
            nextAction === 'in' ? t('attendance.toast_in_ok') : t('attendance.toast_out_ok'),
          );
        },
        onError: (e) => {
          const msg =
            (e as { response?: { data?: { message?: string | string[] } } }).response?.data?.message;
          toast.error(Array.isArray(msg) ? msg.join(', ') : msg ?? t('attendance.toast_error'));
        },
      },
    );
  }

  return (
    <div className="att-page">
      <div className="att-hero">
        <div className="att-date">{todayLabel()}</div>
        <div className="att-greeting">{t('attendance.greeting', { name: user.displayName })}</div>
      </div>

      <div className="att-status-card" data-state={clockedOut ? 'done' : clockedIn ? 'in' : 'pending'}>
        <div className="att-status-icon">
          <span className="material-symbols-outlined">
            {clockedOut ? 'check_circle' : clockedIn ? 'schedule' : 'fingerprint'}
          </span>
        </div>
        <div className="att-status-label">
          {clockedOut
            ? t('attendance.state_done')
            : clockedIn
              ? t('attendance.state_in')
              : t('attendance.state_pending')}
        </div>
        {today?.status && today.status !== 'NORMAL' && (
          <div className="att-status-badge" data-kind={today.status}>
            {t(`attendance.status_${today.status.toLowerCase()}`)}
          </div>
        )}
      </div>

      <div className="att-timeline">
        <div className="att-row">
          <div className="att-row-label">{t('attendance.clock_in_at')}</div>
          <div className="att-row-value">{formatClock(today?.clockIn ?? null)}</div>
        </div>
        <div className="att-row">
          <div className="att-row-label">{t('attendance.clock_out_at')}</div>
          <div className="att-row-value">{formatClock(today?.clockOut ?? null)}</div>
        </div>
        {typeof today?.lateMinutes === 'number' && today.lateMinutes > 0 && (
          <div className="att-row">
            <div className="att-row-label">{t('attendance.late_minutes')}</div>
            <div className="att-row-value">{today.lateMinutes} min</div>
          </div>
        )}
      </div>

      <button
        type="button"
        className="att-action-btn"
        data-action={nextAction}
        disabled={!canAct || clock.isPending || isLoading}
        onClick={onClock}
      >
        <span className="material-symbols-outlined">
          {nextAction === 'in' ? 'login' : 'logout'}
        </span>
        {clock.isPending
          ? t('attendance.processing')
          : nextAction === 'in'
            ? t('attendance.action_clock_in')
            : t('attendance.action_clock_out')}
      </button>

      {clockedOut && (
        <div className="att-done-note">{t('attendance.done_note')}</div>
      )}
    </div>
  );
}
